import React, { useState } from 'react';
import { useDispatch } from 'react-redux';

import {
  Box,
  Button,
  CircularProgress,
  Divider,
  TextField,
  Typography
} from '@mui/material';

import TopBar from '../../../../../components/utils/TopBar';
import ConfirmationModal from '../../../../../components/modals/ConfirmationModal';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import CloudSyncOutlinedIcon from '@mui/icons-material/CloudSyncOutlined';

import {
  IngestCh99PdfBIZcoreTD,
  WipeCh99TablesBIZcoreTD
} from '../../../../../redux/actions/business/core/ch99IngestActions';

import * as api from '../../../../../api/index';
import { toast } from 'react-toastify';

const IS_LOCAL = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';

const REMOTE_TARGETS = [
  { label: 'Alpha', url: process.env.REACT_APP_ALPHA_CORE_URL || 'https://alpha-api.lusid.ai/us/core/web/v1/trd/ch99-import' },
  { label: 'Production', url: process.env.REACT_APP_PROD_CORE_URL || 'https://api.lusidpay.com/us/core/web/v1/trd/ch99-import' },
];

const Ch99IngestPage = () => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [file, setFile] = useState(null);
  const [revisionName, setRevisionName] = useState("");
  const [effectiveDate, setEffectiveDate] = useState("");
  const [result, setResult] = useState(null);

  const [pushing, setPushing] = useState(null);
  const [pushResult, setPushResult] = useState(null);

  const [wipeConfirmOpen, setWipeConfirmOpen] = useState(false);
  const [wiping, setWiping] = useState(false);

  const handleIngest = async () => {
    if (!file || !revisionName || !effectiveDate) return;
    const formData = new FormData();
    formData.append("file", file);
    formData.append("revision_name", revisionName);
    formData.append("effective_date", effectiveDate);
    setResult(null);
    setPushResult(null);
    setLoading(true);
    await dispatch(IngestCh99PdfBIZcoreTD(formData, setLoading, setResult));
    setLoading(false);
  };

  const handlePushToRemote = async (target) => {
    if (!target.url) {
      toast.error(`No URL configured for ${target.label}`);
      return;
    }
    setPushing(target.label);
    setPushResult(null);
    try {
      toast.info(`Exporting local data...`);
      const { data: exportResp } = await api.exportCh99DataBIZcoreTD();
      const exportData = exportResp.data;

      if (!exportData || !exportData.nodes || exportData.nodes.length === 0) {
        toast.error('No local data to push. Ingest a PDF first.');
        setPushing(null);
        return;
      }

      toast.info(`Pushing ${exportData.stats.nodes} nodes + ${exportData.stats.contents} content rows to ${target.label}...`);
      const { data: importResp, status } = await api.pushCh99DataToRemote(target.url, exportData);

      if (status === 201) {
        toast.success(`Pushed to ${target.label} successfully`);
        setPushResult({
          target: target.label,
          nodes: importResp.data?.nodes,
          contents: importResp.data?.contents,
          revisions: importResp.data?.revisions,
          revision_statuses: importResp.data?.revision_statuses,
          segmentation_runs: importResp.data?.segmentation_runs,
          furniture_logs: importResp.data?.furniture_logs,
        });
      } else {
        toast.error(`Push to ${target.label} failed`);
      }
    } catch (error) {
      console.error(error);
      toast.error(`Push to ${target.label} failed: ${error.response?.data?.error || error.message}`);
    }
    setPushing(null);
  };

  const handleWipe = () => {
    setWipeConfirmOpen(false);
    dispatch(WipeCh99TablesBIZcoreTD(setWiping)).then(() => {
      setResult(null);
      setPushResult(null);
    });
  };

  return (
    <Box>

      <ConfirmationModal
        open={wipeConfirmOpen}
        setOpen={setWipeConfirmOpen}
        title={"Wipe all SC tables"}
        onCancel={() => setWipeConfirmOpen(false)}
        onConfirm={handleWipe}
      />

      <TopBar
        title={"Chapter 99 PDF Ingestion"}
        button3Text={wiping ? "Wiping..." : "Wipe SC tables"}
        button3OnClick={() => setWipeConfirmOpen(true)}
      />

      {IS_LOCAL ? (
        <Box className="block" p={"24px"}>
          <Box display={"flex"} flexDirection={"column"} gap={"20px"} maxWidth={"500px"}>

            <Typography variant="body2" color={"grey"}>
              Process the PDF locally, then push the extracted data to Alpha or Production.
            </Typography>

            <Button variant="outlined" component="label" startIcon={<CloudUploadOutlinedIcon />} disabled={loading}>
              {file ? file.name : "Select PDF file"}
              <input type="file" accept=".pdf" hidden onChange={(e) => { if (e.target.files?.[0]) setFile(e.target.files[0]); }} />
            </Button>

            <TextField label="Revision name" placeholder="e.g. 2026 HTS Revision 15" size="small"
              value={revisionName} onChange={(e) => setRevisionName(e.target.value)} disabled={loading} />

            <TextField label="Effective date" size="small" type="date" InputLabelProps={{ shrink: true }}
              value={effectiveDate} onChange={(e) => setEffectiveDate(e.target.value)} disabled={loading} />

            <Button variant="contained" onClick={handleIngest}
              disabled={loading || !file || !revisionName || !effectiveDate}
              sx={{ alignSelf: "flex-start" }}>
              {loading ? <CircularProgress size={20} sx={{ mr: 1 }} /> : null}
              {loading ? "Processing locally..." : "Process PDF Locally"}
            </Button>

            {result && (
              <Box sx={{ border: "1px solid #334155", borderRadius: "8px", overflow: "hidden" }}>
                <Box sx={{ px: "16px", py: "10px", background: "#166534", borderBottom: "1px solid #14532d" }}>
                  <Typography variant="subtitle2" color="#fff">Local Processing Result</Typography>
                </Box>
                <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 0 }}>
                  {[
                    { label: "Total pages", value: result.total_pages },
                    { label: "Subchapters", value: result.subchapters },
                    { label: "Notes", value: result.notes },
                    { label: "Subdivisions", value: result.subdivisions },
                    { label: "Subprovisions", value: result.subprovisions },
                    { label: "Tables", value: result.tables },
                  ].map((item, i) => (
                    <Box key={i} sx={{ px: "16px", py: "10px", borderBottom: "1px solid #334155" }}>
                      <Typography variant="caption" color="#94a3b8">{item.label}</Typography>
                      <Typography variant="body1" fontWeight={700} color="#fff">
                        {(item.value ?? 0).toLocaleString()}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            )}

            {result && (
              <>
                <Divider sx={{ borderColor: '#334155' }} />
                <Typography variant="body2" color={"grey"}>
                  Push the locally processed data to a remote environment:
                </Typography>
                <Box display={"flex"} gap={"12px"}>
                  {REMOTE_TARGETS.filter(t => t.url).map((target) => (
                    <Button
                      key={target.label}
                      variant="outlined"
                      startIcon={pushing === target.label ? <CircularProgress size={16} /> : <CloudSyncOutlinedIcon />}
                      disabled={!!pushing}
                      onClick={() => handlePushToRemote(target)}
                    >
                      {pushing === target.label ? `Pushing to ${target.label}...` : `Push to ${target.label}`}
                    </Button>
                  ))}
                </Box>
              </>
            )}

            {pushResult && (
              <Box sx={{ border: "1px solid #334155", borderRadius: "8px", overflow: "hidden" }}>
                <Box sx={{ px: "16px", py: "10px", background: "#1e40af" }}>
                  <Typography variant="subtitle2" color="#fff">Pushed to {pushResult.target}</Typography>
                </Box>
                <Box sx={{ px: "16px", py: "12px" }}>
                  <Typography variant="body2" color="#94a3b8">
                    {pushResult.nodes} nodes, {pushResult.contents} contents, {pushResult.revisions} revisions, {pushResult.revision_statuses ?? 0} statuses, {pushResult.segmentation_runs ?? 0} seg runs, {pushResult.furniture_logs ?? 0} furniture logs
                  </Typography>
                </Box>
              </Box>
            )}

          </Box>
        </Box>
      ) : (
        <Box className="block" p={"24px"}>
          <Box sx={{ textAlign: 'center', py: '40px' }}>
            <Typography variant="h6" color="#64748b" mb={"8px"}>
              PDF Processing Disabled
            </Typography>
            <Typography variant="body2" color="#475569">
              PDF ingestion must be done from the local environment due to server resource constraints.
              Open the local fe-internal and process the PDF there, then push the data to this environment.
            </Typography>
          </Box>
        </Box>
      )}

    </Box>
  );
};

export default Ch99IngestPage;
